/** Pricing modal catalogs. Existing `data/pricing.js` / `/pricing` are unchanged. */

import { AI_OPERATION_CREDITS } from "./aiOperationCredits";

export const YEARLY_SAVE_PERCENT = 20;

export const billingModes = [
  { id: "onetime", label: "Pay as you go" },
  { id: "monthly", label: "Monthly" },
  { id: "yearly", label: "Yearly" },
];

/** Pay-as-you-go account features (same for every credit pack). */
const onetimePackFeatures = {
  Storage: "50 GB",
  "Concurrent tasks": "5",
  "Batch size": "50",
  Team: "5",
  "Brand kits": "5",
  "Project folders": "50",
  "Share designs": true,
  "Commercial license": true,
  "Priority support": "Enterprise",
  "API access": false,
  Webhooks: false,
};

export const creditPacks = [
  { credits: 1, price: 1.5 },
  { credits: 10, price: 7.5 },
  { credits: 50, price: 32.5 },
  { credits: 100, price: 50 },
  { credits: 200, price: 80 },
  { credits: 500, price: 180, badge: "Popular" },
  { credits: 1000, price: 300 },
  { credits: 2000, price: 500 },
  { credits: 5000, price: 750 },
  { credits: 10000, price: 1000, badge: "Best value" },
].map((pack) => ({
  id: `c${pack.credits}`,
  ...pack,
  features: onetimePackFeatures,
}));

export const plans = [
  {
    id: "lite",
    name: "Lite",
    accent: "lite",
    credits: 100,
    monthly: 10,
    creditTiers: [
      { credits: 50, monthly: 6 },
      { credits: 100, monthly: 10 },
      { credits: 200, monthly: 16 },
    ],
  },
  {
    id: "pro",
    name: "Pro",
    accent: "pro",
    badge: "Popular",
    credits: 500,
    monthly: 45,
    creditTiers: [
      { credits: 300, monthly: 30 },
      { credits: 500, monthly: 45 },
      { credits: 1000, monthly: 80 },
      { credits: 2500, monthly: 175 },
    ],
  },
  {
    id: "max",
    name: "Max",
    accent: "max",
    credits: 10000,
    monthly: 650,
    creditTiers: [
      { credits: 5000, monthly: 350 },
      { credits: 10000, monthly: 650 },
      { credits: 25000, monthly: 1500 },
      { credits: 50000, monthly: 2750 },
      { credits: 75000, monthly: 3750 },
      { credits: 100000, monthly: 4500 },
    ],
  },
];

export const featureColumns = [
  { id: "onetime", label: "Pay as you go" },
  { id: "lite", label: "Lite" },
  { id: "pro", label: "Pro" },
  { id: "max", label: "Max" },
];

function aiTool(label, creditsPerUse, taskKey, hint) {
  return {
    label,
    creditsPerUse,
    taskKey,
    hint:
      hint ||
      `${creditsPerUse} credit${creditsPerUse === 1 ? "" : "s"} per use`,
  };
}

function creditsPerPlan(payg, subscribed) {
  return {
    onetime: payg,
    lite: subscribed,
    "pro": subscribed,
    max: subscribed,
  };
}

function creditCostHints(costs) {
  return Object.fromEntries(
    Object.entries(costs).map(([id, amount]) => [
      id,
      `${amount} credit${amount === 1 ? "" : "s"} per use`,
    ]),
  );
}

function passportCreditRow(label, credits, addon = false, taskKey = null) {
  const costs = creditsPerPlan(credits, credits);
  return {
    label,
    taskKey,
    creditsPerUse: costs,
    showCreditCost: addon,
    hint: addon
      ? "Added to the standard passport photo cost."
      : "Uses your plan credits.",
    valueHints: addon ? undefined : creditCostHints(costs),
  };
}

const ALL_INCLUDED = {
  onetime: true,
  lite: true,
  "pro": true,
  max: true,
};

const ALL_UNLIMITED = {
  onetime: "Unlimited",
  lite: "Unlimited",
  "pro": "Unlimited",
  max: "Unlimited",
};

const ENTERPRISE_ONLY = {
  onetime: "Enterprise",
  lite: "Enterprise",
  "pro": "Enterprise",
  max: "Enterprise",
};

const BATCH_FROM_PRO = {
  onetime: true,
  lite: false,
  "pro": true,
  max: true,
};

/** Same comparison table for every tab / selection. */
export const featureGroups = [
  {
    id: "credits",
    kind: "pin",
    label: "Credits & Limits",
    rows: [
      {
        label: "Ai Credits",
        hint: "How many credits this plan includes. Use the slider to pick a higher or lower amount.",
        values: {
          onetime: "pack",
          lite: "slider",
          "pro": "slider",
          max: "slider",
        },
      },
      {
        label: "Per image",
        values: {
          onetime: "pack",
          lite: "slider",
          "pro": "slider",
          max: "slider",
        },
      },
      {
        label: "Credits Rollover",
        hint: "Unused credits rollover as long as you're subscribed.",
        values: {
          onetime: "N/A",
          lite: "Up to 3x",
          "pro": "Up to 5x",
          max: "Up to 6x",
        },
        valueHints: {
          onetime: "No monthly plan, so credits don't rollover. They never expire.",
          lite: "Rollover up to 3x your monthly credits. They stay until your subscription is active.",
          "pro": "Rollover up to 5x your monthly credits. They stay until your subscription is active.",
          max: "Rollover up to 6x your monthly credits. They stay until your subscription is active.",
        },
      },
      {
        label: "Change plan",
        hint: "Downgrade, upgrade, or cancel anytime.",
        values: {
          onetime: "N/A",
          lite: "Anytime",
          "pro": "Anytime",
          max: "Anytime",
        },
      },
    ],
  },
  {
    id: "access",
    kind: "pin",
    label: "Access",
    rows: [
      {
        label: "Image resolution",
        values: { onetime: "4K", lite: "2K", "pro": "4K", max: "4K" },
      },
      {
        label: "Exports",
        values: {
          onetime: "pack",
          lite: "slider",
          "pro": "slider",
          max: "slider",
        },
      },
      {
        label: "Share designs",
        values: { onetime: true, lite: true, "pro": true, max: true },
      },
      {
        label: "Commercial license",
        values: { onetime: true, lite: true, "pro": true, max: true },
      },
    ],
  },
  {
    id: "playground",
    kind: "pin",
    label: "Playground",
    rows: [
      {
        label: "Storage",
        values: {
          onetime: "50 GB",
          lite: "2 GB",
          "pro": "100 GB",
          max: "500 GB",
        },
      },
      {
        label: "Concurrent tasks",
        values: { onetime: "5", lite: "2", "pro": "5", max: "10" },
      },
      {
        label: "Batch size",
        values: { onetime: "50", lite: false, "pro": "50", max: "250" },
      },
      {
        label: "Team",
        values: { onetime: "5", lite: "5", "pro": "25", max: "50" },
      },
      {
        label: "Brand kits",
        values: { onetime: "5", lite: "2", "pro": "10", max: "50" },
      },
      {
        label: "Project folders",
        values: { onetime: "50", lite: "5", "pro": "100", max: "500" },
      },
    ],
  },
  {
    id: "editing-studio",
    kind: "studio",
    label: "Ai Editing Studio",
    summary: { onetime: true, lite: true, "pro": true, max: true },
    sections: [
      {
        label: "Ai Tools",
        hint: "How many times you can run this tool if you spend all your credits on it.",
        rows: [
          aiTool("Ai remove background", 1, "remove_bg"),
          aiTool("Ai background images", 2, "ai_background"),
          aiTool(
            "Ai upscale",
            2,
            "upscale",
            "2× is 2 credits. 4× is 4 credits. Balanced adds 1 credit. Best adds 2 credits.",
          ),
          aiTool("Ai expand", 2, "outpaint"),
          aiTool("Ai shadow", 2, "ai_shadow"),
          aiTool("Ai recolor", 2, "recolor"),
          aiTool("Ai remove object", 2, "remove_object"),
          aiTool("Ai replace object", 2, "replace_object"),
          aiTool("Ai remove text from images", 1, "ai_remove_text"),
          aiTool("Ai enhance", 1, "ai_enhance"),
          aiTool("Ai relight", 1, "relight"),
          aiTool("Ai watermark remover", 1, "ai_remove_watermark"),
        ],
      },
      {
        label: "Editing Tools",
        rows: [
          { label: "Resize", values: ALL_UNLIMITED },
          { label: "Shadow", values: ALL_UNLIMITED },
          { label: "Outline", values: ALL_UNLIMITED },
          { label: "Background color", values: ALL_UNLIMITED },
          { label: "Background images", values: ALL_UNLIMITED },
          { label: "Rotate", values: ALL_UNLIMITED },
          { label: "Flip", values: ALL_UNLIMITED },
          { label: "Padding", values: ALL_UNLIMITED },
          { label: "Crop", values: ALL_UNLIMITED },
          { label: "Circle Crop", values: ALL_UNLIMITED },
          { label: "Watermark Maker", values: ALL_UNLIMITED },
          { label: "Blur", values: ALL_UNLIMITED },
          { label: "Gaussian Blur", values: ALL_UNLIMITED },
          { label: "Pixel Blur", values: ALL_UNLIMITED },
          { label: "Motion Blur", values: ALL_UNLIMITED },
          { label: "Bokeh Blur", values: ALL_UNLIMITED },
        ],
      },
      {
        label: "Batch Tools",
        hint: "Apply these tools across many images at once. Not included on Lite.",
        rows: [
          { label: "Resize", values: BATCH_FROM_PRO },
          { label: "Positioning", values: BATCH_FROM_PRO },
          { label: "Shadow", values: BATCH_FROM_PRO },
          { label: "Outline", values: BATCH_FROM_PRO },
          { label: "Background color", values: BATCH_FROM_PRO },
          { label: "Background images", values: BATCH_FROM_PRO },
          { label: "Rotate", values: BATCH_FROM_PRO },
          { label: "Flip", values: BATCH_FROM_PRO },
        ],
      },
    ],
  },
  {
    id: "passport-studio",
    kind: "studio",
    label: "Ai Photo Studio",
    summary: ALL_INCLUDED,
    sections: [
      {
        label: "Passport photo",
        hint: "How many photos you can make if you spend all your credits on this tool. Add-ons cost extra.",
        rows: [
          {
            ...passportCreditRow("Ai Passport (low quality)", 5, false, "passport_photo_low"),
            hint: "Includes AI face detection and crop. Uses your plan credits.",
          },
          passportCreditRow("Ai Passport (medium quality)", 10, false, "passport_photo_medium"),
          passportCreditRow("Ai Passport (high quality)", 25, false, "passport_photo_high"),
          passportCreditRow("Clothing selection (Add on)", 10, true, "passport_addon_clothing"),
          passportCreditRow("Face retouch (Add on)", 10, true, "passport_addon_retouch"),
          passportCreditRow("Eyewear removal (Add on)", 5, true, "passport_addon_eyewear"),
          passportCreditRow("Smile correction (Add on)", 5, true, "passport_addon_smile"),
          passportCreditRow("Hair correction (Add on)", 5, true, "passport_addon_hair"),
        ],
      },
      {
        label: "Included",
        rows: [
          { label: "Country-specific sizes", values: ALL_INCLUDED },
          { label: "100% compliance", values: ALL_INCLUDED },
          { label: "Compliant backgrounds", values: ALL_INCLUDED },
          { label: "Digital image", values: ALL_INCLUDED },
          { label: "Print-ready downloads", values: ALL_INCLUDED },
        ],
      },
    ],
  },
  {
    id: "document-tools",
    kind: "studio",
    label: "Document Tools",
    summary: ALL_INCLUDED,
    sections: [
      {
        label: null,
        rows: [
          { label: "Sign PDF (Basic)", values: ALL_UNLIMITED },
          {
            label: "Sign PDF (Pro) - Soon",
            values: { onetime: "Soon", lite: "Soon", pro: "Soon", max: "Soon" },
          },
          { label: "PDF Watermarker", values: ALL_UNLIMITED },
          { label: "PDF Page numbers", values: ALL_UNLIMITED },
          { label: "Watermark Word Document", values: ALL_UNLIMITED },
        ],
      },
    ],
  },
  {
    id: "performance",
    kind: "pin",
    label: "Performance",
    rows: [
      { label: "Priority support", values: ENTERPRISE_ONLY },
      { label: "Visual QA", values: ENTERPRISE_ONLY },
      { label: "ROI data", values: ENTERPRISE_ONLY },
      { label: "SLA / SSO", values: ENTERPRISE_ONLY },
      { label: "Onboarding", values: ENTERPRISE_ONLY },
      {
        label: "API access",
        values: { onetime: false, lite: false, "pro": false, max: true },
      },
      {
        label: "Webhooks",
        values: { onetime: false, lite: false, "pro": false, max: true },
      },
    ],
  },
];

export const DEFAULT_STUDIO_ID = "editing-studio";

export const featureRows = featureGroups.flatMap((group) =>
  (group.sections ?? [{ rows: group.rows }]).flatMap((section) => section.rows ?? []),
);

export function isSubscription(billing) {
  return billing === "monthly" || billing === "yearly";
}

/** Approximate USD rates for display only — billing still settles in USD until checkout is wired. */
export const pricingCountries = [
  { id: "us", name: "United States", currency: "USD", symbol: "$", rate: 1 },
  { id: "in", name: "India", currency: "INR", symbol: "₹", rate: 95 },
  { id: "gb", name: "United Kingdom", currency: "GBP", symbol: "£", rate: 0.79 },
  { id: "ca", name: "Canada", currency: "CAD", symbol: "CA$", rate: 1.36 },
  { id: "au", name: "Australia", currency: "AUD", symbol: "A$", rate: 1.53 },
  { id: "de", name: "Germany", currency: "EUR", symbol: "€", rate: 0.92 },
  { id: "fr", name: "France", currency: "EUR", symbol: "€", rate: 0.92 },
  { id: "it", name: "Italy", currency: "EUR", symbol: "€", rate: 0.92 },
  { id: "ie", name: "Ireland", currency: "EUR", symbol: "€", rate: 0.92 },
  { id: "es", name: "Spain", currency: "EUR", symbol: "€", rate: 0.92 },
  { id: "nl", name: "Netherlands", currency: "EUR", symbol: "€", rate: 0.92 },
  { id: "sg", name: "Singapore", currency: "SGD", symbol: "S$", rate: 1.34 },
  { id: "nz", name: "New Zealand", currency: "NZD", symbol: "NZ$", rate: 1.66 },
  { id: "cn", name: "China", currency: "CNY", symbol: "¥", rate: 7.24 },
  { id: "jp", name: "Japan", currency: "JPY", symbol: "¥", rate: 149 },
  { id: "ae", name: "United Arab Emirates", currency: "AED", symbol: "AED ", rate: 3.67 },
  { id: "ch", name: "Switzerland", currency: "CHF", symbol: "CHF ", rate: 0.88 },
  { id: "br", name: "Brazil", currency: "BRL", symbol: "R$", rate: 5.6 },
  { id: "mx", name: "Mexico", currency: "MXN", symbol: "MX$", rate: 17.2 },
  { id: "za", name: "South Africa", currency: "ZAR", symbol: "R", rate: 18.5 },
  { id: "ph", name: "Philippines", currency: "PHP", symbol: "₱", rate: 58 },
  { id: "my", name: "Malaysia", currency: "MYR", symbol: "RM", rate: 4.7 },
  { id: "th", name: "Thailand", currency: "THB", symbol: "฿", rate: 36 },
  { id: "kr", name: "South Korea", currency: "KRW", symbol: "₩", rate: 1380 },
  { id: "hk", name: "Hong Kong", currency: "HKD", symbol: "HK$", rate: 7.82 },
];

const ZERO_DECIMAL = new Set(["JPY", "KRW", "VND", "IDR"]);

export function getPricingCountry(id) {
  return pricingCountries.find((country) => country.id === id) || pricingCountries[0];
}

/** India → INR; everywhere else → USD. No user-facing currency picker. */
export function detectPricingCountryId() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const langs = [navigator.language, ...(navigator.languages || [])]
      .filter(Boolean)
      .map((item) => String(item).toLowerCase());
    if (
      tz === "Asia/Kolkata" ||
      tz === "Asia/Calcutta" ||
      langs.some((lang) => lang === "hi" || lang.startsWith("hi-") || lang.endsWith("-in"))
    ) {
      return "in";
    }
  } catch (_) {
    /* browser-only; default USD */
  }
  return "us";
}

export function countryIdForCurrency(code) {
  return String(code || "").toUpperCase() === "INR" ? "in" : "us";
}

export function catalogMajor(sku, currency, usdFallback = 0) {
  const code = String(currency || "USD").toUpperCase();
  const row = (sku?.prices || []).find(
    (price) => String(price.currency || "").toUpperCase() === code,
  );
  if (row?.amount != null) return Number(row.amount);
  if (code === "INR") {
    return sku?.price_inr != null ? Number(sku.price_inr) : null;
  }
  return Number(
    sku?.price ?? sku?.credit_pack_price ?? sku?.package_price ?? usdFallback ?? 0,
  );
}

export function formatCurrencyAmount(amount, currency) {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  const code = String(currency || "USD").toUpperCase();
  const value = Number(amount);
  const zero = ZERO_DECIMAL.has(code);
  const rounded = Math.round(value * 100) / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: code,
    minimumFractionDigits: zero || Number.isInteger(rounded) ? 0 : 2,
    maximumFractionDigits: zero ? 0 : 2,
  }).format(value);
}

export function formatMoney(amountUsd, country) {
  if (amountUsd == null || Number.isNaN(Number(amountUsd))) return "—";
  const selected = country || pricingCountries[0];
  const value = Number(amountUsd) * selected.rate;
  return formatCurrencyAmount(value, selected.currency);
}

export function formatUsd(amount) {
  return formatMoney(amount, pricingCountries[0]);
}

export function generationsFromCredits(credits, creditsPerUse) {
  const cost = Number(creditsPerUse);
  if (!credits || !cost) return "—";
  return Math.floor(credits / cost).toLocaleString();
}

export function perImageLabel(price, credits, country) {
  if (!price || !credits) return "—";
  const selected = country || pricingCountries[0];
  const major =
    selected.currency === "USD" || selected.currency === "INR"
      ? Number(price)
      : Number(price) * selected.rate;
  const value = major / credits;
  if (ZERO_DECIMAL.has(selected.currency)) {
    return `${selected.symbol}${Math.round(value)}`;
  }
  if (value >= 0.1) return `${selected.symbol}${value.toFixed(2)}`;
  return `${selected.symbol}${value.toFixed(3).replace(/0+$/, "").replace(/\.$/, "")}`;
}

export function yearlyFromMonthly(monthly) {
  const originalYearly = monthly * 12;
  const yearlyTotal = originalYearly * (1 - YEARLY_SAVE_PERCENT / 100);
  return {
    originalYearly,
    yearlyTotal,
    perMonth: yearlyTotal / 12,
  };
}

export function getPlanTiers(planId) {
  return plans.find((plan) => plan.id === planId)?.creditTiers ?? [];
}

/** @deprecated Prefer getPlanTiers("max") */
export function getMaxTiers() {
  return getPlanTiers("max");
}

export function resolvePlanTier(planId, credits) {
  const tiers = getPlanTiers(planId);
  if (!tiers.length) {
    const plan = plans.find((item) => item.id === planId);
    return plan
      ? { credits: plan.credits, monthly: plan.monthly }
      : { credits: 0, monthly: 0 };
  }
  return tiers.find((tier) => tier.credits === credits) || tiers[0];
}

export const PLAN_FAMILY_RANK = { lite: 1, pro: 2, max: 3 };

export function planSortKey(family, credits) {
  const rank = PLAN_FAMILY_RANK[String(family || "").toLowerCase()] || 0;
  return rank * 1_000_000 + Number(credits || 0);
}

export function comparePlanSelection(current, selected) {
  if (!current?.family) return "new";
  const a = planSortKey(current.family, current.credits);
  const b = planSortKey(selected?.family, selected?.credits);
  if (a === b) return "current";
  if (b > a) return "upgrade";
  return "downgrade";
}

export function currentPlanFromBenefits(benefits) {
  if (!benefits?.has_subscription) return null;
  const fromName = String(benefits.plan_name || "").toLowerCase();
  let family = String(
    benefits.plan_family || benefits.plan_type || "",
  ).toLowerCase();
  if (!PLAN_FAMILY_RANK[family]) {
    if (fromName.includes("max")) family = "max";
    else if (fromName.includes("pro")) family = "pro";
    else if (fromName.includes("lite")) family = "lite";
  }
  if (!PLAN_FAMILY_RANK[family]) return null;
  const credits = Number(benefits.monthly_credits || 0);
  if (!credits) return null;
  return { family, credits, packageId: benefits.package_id ?? null };
}

/** @deprecated Prefer resolvePlanTier("max", credits) */
export function resolveMaxTier(credits) {
  return resolvePlanTier("max", credits);
}

export function defaultTierCredits() {
  return Object.fromEntries(
    plans.map((plan) => [
      plan.id,
      plan.credits ?? plan.creditTiers?.[0]?.credits ?? 0,
    ]),
  );
}

/** Editing-tool download quality upgrades (mirrors config/tools/downloadQualityTiers). */
export {
  DOWNLOAD_QUALITY_TIERS,
  getDownloadQualityTier,
  buildDownloadQualityOptions,
} from "../config/tools/downloadQualityTiers";

/** Flat tool cost from a GET /api/pricing tools[] row. */
export function creditCostForTool(tool, _audience = "payg") {
  if (!tool) return null;
  const value = tool.credit_cost ?? tool.costs?.payg ?? tool.costs?.subscribed;
  return value == null ? null : Number(value);
}

/**
 * Static catalog shaped like GET /api/pricing.
 * New tools: add a featureGroups row with `taskKey` matching tasks.task_name.
 */
export function buildStaticPricingCatalog() {
  const tools = [];
  for (const group of featureGroups) {
    const sections = group.sections ?? [{ rows: group.rows || [] }];
    for (const section of sections) {
      for (const row of section.rows || []) {
        if (!row.taskKey) continue;
        const cpu = row.creditsPerUse;
        const payg = typeof cpu === "object" ? Number(cpu.onetime) : Number(cpu);
        const subscribed =
          typeof cpu === "object"
            ? Number(cpu.pro ?? cpu.lite ?? cpu.onetime)
            : payg;
        tools.push({
          key: row.taskKey,
          label: row.label,
          credit_cost: payg,
          costs: {
            payg,
            subscribed,
            lite: subscribed,
            pro: subscribed,
            max: subscribed,
          },
        });
      }
    }
  }
  return {
    success: true,
    packs: creditPacks.map((pack, index) => ({
      id: pack.id,
      credits: pack.credits,
      credits_allotted: pack.credits,
      price: pack.price,
      credit_pack_price: pack.price,
      currency: "USD",
      display_order: index + 1,
      name: `${pack.credits} Credit${pack.credits === 1 ? "" : "s"}`,
    })),
    plans: plans.flatMap((plan) =>
      (plan.creditTiers || [{ credits: plan.credits, monthly: plan.monthly }]).map(
        (tier, index) => ({
          family: plan.id,
          name: plan.name,
          credits: tier.credits,
          credits_allotted: tier.credits,
          price: tier.monthly,
          package_price: tier.monthly,
          billing_period: "month",
          currency: "USD",
          display_order: index,
          subscription_name: `${plan.name} ${tier.credits}`,
        }),
      ),
    ),
    tools,
    plan_families: [
      { id: "payg", slug: "payg", name: "Pay as you go", storage_quota_gb: 50, exports_allotted: 500 },
      { id: "lite", slug: "lite", name: "Lite", storage_quota_gb: 2, exports_allotted: 100 },
      { id: "pro", slug: "pro", name: "Pro", storage_quota_gb: 100, exports_allotted: 2000 },
      { id: "max", slug: "max", name: "Max", storage_quota_gb: 500, exports_allotted: 10000 },
    ],
  };
}

/** Overlay Storage / Exports rows from GET /api/pricing plan_families. */
export function applyPlanFamiliesToFeatureGroups(groups, planFamilies) {
  if (!Array.isArray(planFamilies) || !planFamilies.length) return groups;
  const bySlug = Object.fromEntries(
    planFamilies
      .filter((f) => f?.slug || f?.id)
      .map((f) => [String(f.slug || f.id).toLowerCase(), f]),
  );
  const storageValues = {
    onetime: formatGbLabel(bySlug.payg?.storage_quota_gb ?? 50),
    lite: formatGbLabel(bySlug.lite?.storage_quota_gb ?? 2),
    pro: formatGbLabel(bySlug.pro?.storage_quota_gb ?? 100),
    max: formatGbLabel(bySlug.max?.storage_quota_gb ?? 500),
  };
  const exportValues = {
    onetime: formatExportLabel(bySlug.payg?.exports_allotted),
    lite: formatExportLabel(bySlug.lite?.exports_allotted),
    pro: formatExportLabel(bySlug.pro?.exports_allotted),
    max: formatExportLabel(bySlug.max?.exports_allotted),
  };

  return groups.map((group) => {
    const mapRows = (rows) =>
      (rows || []).map((row) => {
        if (row.label === "Storage") return { ...row, values: storageValues };
        if (row.label === "Exports") return { ...row, values: exportValues };
        return row;
      });
    if (group.sections) {
      return {
        ...group,
        sections: group.sections.map((section) => ({
          ...section,
          rows: mapRows(section.rows),
        })),
      };
    }
    return { ...group, rows: mapRows(group.rows) };
  });
}

function formatGbLabel(gb) {
  const n = Number(gb);
  if (!Number.isFinite(n) || n < 0) return "—";
  return `${n} GB`;
}

function formatExportLabel(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v < 0) return "—";
  if (v === 0) return "Unlimited";
  return String(v);
}

export function applyToolCostsToFeatureGroups(groups, tools) {
  if (!Array.isArray(tools) || !tools.length) return groups;
  const byKey = Object.fromEntries(
    tools.filter((tool) => tool?.key).map((tool) => [tool.key, tool]),
  );

  const applyRow = (row) => {
    if (row.taskKey === "upscale" || AI_OPERATION_CREDITS[row.taskKey] != null) {
      return row;
    }
    const tool = row.taskKey ? byKey[row.taskKey] : null;
    if (!tool) return row;
    const cost = Number(tool.credit_cost ?? tool.costs?.payg);
    if (Number.isNaN(cost)) return row;
    const payg = cost;
    const subscribed = cost;
    if (typeof row.creditsPerUse === "object") {
      return {
        ...row,
        creditsPerUse: creditsPerPlan(payg, subscribed),
        valueHints: row.showCreditCost
          ? row.valueHints
          : creditCostHints(creditsPerPlan(payg, subscribed)),
      };
    }
    return {
      ...row,
      creditsPerUse: payg,
      hint: `${payg} credit${payg === 1 ? "" : "s"} per use`,
    };
  };

  return groups.map((group) => ({
    ...group,
    sections: group.sections
      ? group.sections.map((section) => ({
          ...section,
          rows: (section.rows || []).map(applyRow),
        }))
      : group.sections,
    rows: group.rows ? group.rows.map(applyRow) : group.rows,
  }));
}

